import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LogOut,
  Bot,
  ShieldCheck,
  CreditCard,
  Bell,
  ChevronRight,
  Phone,
  PhoneForwarded,
  Siren,
} from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../../__generated__/schema-lite';
import { useLogout } from '../../hooks/useLogout';
import { useAccountBase, useCurrentAccount } from '../../account/AccountContext';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { ThemeSwitcher } from '../../components/ThemeSwitcher';
import { PushToggle } from '../../components/PushToggle';
import { AppVersion } from '../../components/AppVersion';
import { useCan } from '../../account/capabilities';

/** The customer's settings screen — and, more importantly, the only place they can
 *  sign out.
 *
 *  Sign-out used to live in the sidebar footer, which is now admin-only, so
 *  splitting the shells left the customer with no way out of the app at all. It
 *  belongs here anyway: that is where the mockup puts it.
 *
 *  The four rows below are the sub-screens that do not exist yet (assistant,
 *  privacy/retention, subscription, notifications). They are rendered as inert rows
 *  rather than hidden, so the shape of what is coming is visible — and rather than
 *  as links, so nothing leads to a dead route.
 *
 *  Each carries the capability it will need, so a delegate sees the settings of
 *  someone else's line as that line's owner has actually delegated them: a viewer
 *  helping with Ivan's calls has no business reading his invoices or changing how his
 *  assistant answers. Notifications is per person and always shown — the DND window
 *  and the opt-out are the delegate's own, not the owner's to hold. */
// Each setup step reopens the onboarding wizard in edit mode on that exact step
// (`?edit=1&step=<i>`) — that is where the data for the step actually lives, so
// editing reuses the wizard rather than a second set of editors. Gated by
// `configureAssistant` (OWNER/ADMIN); a delegate does not edit someone's setup.
const STEP_LINKS = [
  { icon: Phone, label: 'settings.steps.number', step: 0 },
  { icon: PhoneForwarded, label: 'settings.steps.forwarding', step: 1 },
  { icon: Bot, label: 'settings.steps.assistant', step: 2 },
  { icon: Siren, label: 'settings.steps.topics', step: 3 },
] as const;

// Real account sub-screens that navigate (as opposed to the inert placeholders
// below). Subscription & usage is live — OWNER-gated by `viewBilling`.
const ACCOUNT_LINKS = [
  { icon: CreditCard, label: 'settings.billing', to: 'subscription', capability: 'viewBilling' },
] as const;

// The sub-screens that still do not exist — rendered as inert rows so the shape of
// what is coming is visible without leading to a dead route.
const PLACEHOLDERS = [
  { icon: ShieldCheck, label: 'settings.privacy', capability: 'configureAccount' },
  { icon: Bell, label: 'settings.notifications', capability: null },
] as const;

export default function CustomerSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const base = useAccountBase();
  const { accountId, e164 } = useCurrentAccount();
  const logout = useLogout();
  const crud = useClientQueries(schema);
  const { data: me } = crud.$procs.me.useQuery();
  const user = me as { name?: string | null; email?: string } | undefined;
  // The customer's own number lives on AssistantConfig; the redirect (shadow)
  // number is the line's ACTIVE number, carried on the account context.
  const cfgQ = crud.assistantConfig.useFindFirst({ where: { accountId } });
  const ownNumber = (cfgQ.data as { ownNumber?: string | null } | undefined)?.ownNumber ?? null;
  const can = useCan();
  const canEditSteps = can('configureAssistant');
  const accountLinks = ACCOUNT_LINKS.filter((s) => !s.capability || can(s.capability));
  const placeholders = PLACEHOLDERS.filter((s) => !s.capability || can(s.capability));

  async function signOut() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="h-full overflow-y-auto px-4 pt-6 pb-24 max-w-md mx-auto space-y-6">
      <h1 className="text-xl font-semibold">{t('nav.settings')}</h1>

      {/* Who you are signed in as — the thing you check before signing out. Shows
       *  your own number above the email, plus the shadow line calls forward to. */}
      <div className="rounded-lg border border-border bg-card px-4 py-3 space-y-0.5">
        <div className="font-medium truncate">{user?.name || user?.email || '—'}</div>
        {user?.name && user?.email ? (
          <div className="text-sm text-muted-foreground truncate">{user.email}</div>
        ) : null}
        {ownNumber ? (
          <div className="text-sm text-muted-foreground truncate">
            {t('settings.ownNumber')}: <span className="font-mono">{ownNumber}</span>
          </div>
        ) : null}
        {e164 ? (
          <div className="text-sm text-muted-foreground truncate">
            {t('settings.redirectNumber')}: <span className="font-mono">{e164}</span>
          </div>
        ) : null}
      </div>

      {/* Labelled, because the card above is about the PERSON and these are about the
       *  LINE — and in someone else's line they are that person's assistant, retention
       *  and invoices, not yours. Which line is named by the switcher strip above. */}
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('settings.lineSection')}
      </h2>

      {/* One editable row per wizard step — opens the wizard on that step. */}
      {canEditSteps ? (
        <ul className="rounded-lg border border-border bg-card divide-y divide-border">
          {STEP_LINKS.map(({ icon: Icon, label, step }) => (
            <li key={label}>
              <button
                type="button"
                onClick={() => navigate(`${base}/onboarding?edit=1&step=${step}`)}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted"
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 min-w-0 truncate">{t(label)}</span>
                <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {accountLinks.length > 0 ? (
        <ul className="rounded-lg border border-border bg-card divide-y divide-border">
          {accountLinks.map(({ icon: Icon, label, to }) => (
            <li key={label}>
              <button
                type="button"
                onClick={() => navigate(`${base}/${to}`)}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted"
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 min-w-0 truncate">{t(label)}</span>
                <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {placeholders.length > 0 ? (
        <ul className="rounded-lg border border-border bg-card divide-y divide-border">
          {placeholders.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="px-4 py-3 flex items-center gap-3 text-muted-foreground"
              aria-disabled
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 min-w-0 truncate">{t(label)}</span>
              <span className="text-xs shrink-0">{t('placeholder.soon')}</span>
              <ChevronRight className="h-4 w-4 shrink-0 opacity-40" />
            </li>
          ))}
        </ul>
      ) : null}

      <div className="rounded-lg border border-border bg-card px-4 py-3 space-y-3">
        {/* Per DEVICE, not per account — the subscription belongs to this browser, so
         *  enabling it on a phone says nothing about the laptop. */}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('push.label')}</span>
          <PushToggle />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('nav.language')}</span>
          <LanguageSwitcher />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{t('theme.label')}</span>
          <ThemeSwitcher />
        </div>
      </div>

      <button
        type="button"
        onClick={signOut}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        {t('action.signOut')}
      </button>

      <AppVersion className="mt-3 block text-center text-[11px] text-muted-foreground" />
    </div>
  );
}
